import moment from 'moment/moment'

import { Field } from '../../models/Field'
import { Checkbox } from '../common/fields/Checkbox'
import { CurrencyField } from '../common/fields/CurrencyField'
import { DatePicker } from '../common/fields/DatePicker'
import { FloatField } from '../common/fields/FloatField'
import { IntegerField } from '../common/fields/IntegerField'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { TextArea } from '../common/fields/TextArea'
import { TextField } from '../common/fields/TextField'
import { WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    field: Field
    value: any | undefined
    onChange: (newValue: any) => void
    shouldEnforceRequired: boolean
}

export const CustomFieldEditor = ({ field, value, onChange, shouldEnforceRequired }: Props) => {
    const fieldLabelWithoutTrailingColon = field.label?.endsWith(':')
        ? field.label.substring(0, field.label.length - 1)
        : field.label

    return (
        <WorkflowPage.IconSection
            title={`${fieldLabelWithoutTrailingColon} ${shouldEnforceRequired && field.required ? '(required)' : ''}`}
            icon="form2.large"
            secondaryView={
                field.type === 'checkbox' ? (
                    <Checkbox isChecked={Boolean(value)} onChange={onChange} />
                ) : undefined
            }
        >
            {(() => {
                switch (field.type) {
                    case 'currency': {
                        return (
                            <CurrencyField size="medium" value={value ?? 0} onChange={onChange} />
                        )
                    }
                    case 'checkbox': {
                        return null
                    }
                    case 'email': {
                        return (
                            <TextField
                                size="medium"
                                value={value ?? ''}
                                onChange={(event) => onChange(event.target.value)}
                                placeholder="Enter email"
                                type="email"
                            />
                        )
                    }
                    case 'float': {
                        return <FloatField size="medium" value={value ?? 0} onChange={onChange} />
                    }
                    case 'integer': {
                        return <IntegerField size="medium" value={value ?? 0} onChange={onChange} />
                    }
                    case 'longtext': {
                        return (
                            <TextArea
                                value={value ?? ''}
                                onChange={(event) => onChange(event.target.value)}
                                maxLength={900}
                                rows={3}
                                placeholder="Enter text"
                            />
                        )
                    }
                    case 'phone': {
                        return (
                            <TextField
                                size="medium"
                                value={value ?? ''}
                                onChange={(event) => onChange(event.target.value)}
                                placeholder="Enter mobile number"
                                mask="(000) 000-0000"
                            />
                        )
                    }
                    case 'date': {
                        return (
                            <DatePicker
                                selectedDateString={(value ? moment(value) : moment()).format(
                                    'MM/DD/YYYY'
                                )}
                                onSelectDate={(newDate) =>
                                    onChange(moment(newDate).format('YYYY-MM-DD'))
                                }
                                size="medium"
                            />
                        )
                    }
                    case 'multiselect': {
                        return (
                            <div>
                                <MultiSelectField field={field} value={value} onChange={onChange} />

                                <div className="mt-3 flex flex-col items-stretch justify-start gap-y-3">
                                    {field.options
                                        .filter((option) => (value ?? []).includes(option.id ?? ''))
                                        .map((option) => (
                                            <WorkflowPage.RemovableItemContainer
                                                key={option.id ?? ''}
                                                onRemove={() => {
                                                    onChange(
                                                        (value ?? []).filter(
                                                            (id: string) => id !== option.id
                                                        )
                                                    )
                                                }}
                                                deleteButtonMargin="-right-6"
                                            >
                                                <WorkflowPage.SelectedItem
                                                    title={option.label ?? ''}
                                                />
                                            </WorkflowPage.RemovableItemContainer>
                                        ))}
                                </div>
                            </div>
                        )
                    }
                    case 'select': {
                        return <SelectField field={field} value={value} onChange={onChange} />
                    }
                    case 'text':
                    case '':
                    default:
                        return (
                            <TextField
                                size="medium"
                                value={value ?? ''}
                                onChange={(event) => onChange(event.target.value)}
                                placeholder="Enter text"
                            />
                        )
                }
            })()}
        </WorkflowPage.IconSection>
    )
}

const SelectField = ({
    field,
    value,
    onChange,
}: {
    field: Field
    value: any | undefined
    onChange: (newValue: any) => void
}) => {
    const items = field.options.map((option) => ({
        id: option.id ?? '',
        title: option.label ?? '',
    }))

    return (
        <SearchableSelect
            items={items}
            selectedItemID={value}
            onSelectItem={(option) => onChange(option !== null ? option.id : undefined)}
            size="medium"
            placeholder="Select"
        />
    )
}

const MultiSelectField = ({
    field,
    value,
    onChange,
}: {
    field: Field
    value: any | undefined
    onChange: (newValue: any) => void
}) => {
    const selectedIDs: string[] = value ?? []

    const items = field.options.map((option) => ({
        id: option.id ?? '',
        title: option.label ?? '',
        isChecked: selectedIDs.includes(option.id ?? ''),
    }))

    const handleSelectItem = (item: (typeof items)[number] | null) => {
        if (item === null) {
            return
        }

        if (selectedIDs.some((selectedID) => selectedID === item.id)) {
            onChange(selectedIDs.filter((id) => id !== item.id))
        }
        else {
            onChange([...selectedIDs, item.id])
        }
    }

    return (
        <SearchableSelect
            items={items}
            onSelectItem={handleSelectItem}
            size="medium"
            placeholder="Type to search"
            shouldRefocusAfterSelection
        />
    )
}
